import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Swords, MessageCircle, Link2, Check, Share2, Trophy, Handshake, Clock, Users } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { likeHeaders } from "@/lib/useLikes";
import { storage } from "@/lib/storage";
import { squares } from "@/lib/daily";
import { socialLinks, canNativeShare, nativeShare, copyText } from "@/lib/share";
import { trackEvent } from "@/lib/analytics";
import { warn } from "@/lib/log";

const API = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ "Content-Type": "application/json", ...likeHeaders() });

export const useChallenge = (id) => useQuery({
  queryKey: ["challenge", id],
  queryFn: async () => { const r = await fetch(`${API}/api/daily/challenge/${id}`, { headers: likeHeaders() }); if (!r.ok) throw new Error("challenge"); return r.json(); },
  enabled: !!id, staleTime: 15_000,
});

const friendName = (ch, t) => ch.nickname || t("vs_anon");

/** Banner shown above the quiz when opened through a friend's link. */
export function ChallengeBanner({ id }) {
  const { t } = useLang();
  const { data: ch, isError } = useChallenge(id);
  if (isError) return <div className="rounded-2xl border border-stoneLine bg-sand/60 p-3 text-sm text-muted-foreground" data-testid="vs-banner-missing">{t("vs_missing")}</div>;
  if (!ch) return null;
  if (ch.expired) return <div className="rounded-2xl border border-stoneLine bg-sand/60 p-3 text-sm text-muted-foreground flex items-center gap-2" data-testid="vs-banner-expired"><Clock className="w-4 h-4" /> {t("vs_expired")}</div>;
  return (
    <div className="rounded-2xl border border-ochre/50 bg-ochre/10 p-3 flex items-center gap-3" data-testid="vs-banner">
      <span className="w-9 h-9 rounded-full bg-ochre text-white flex items-center justify-center shrink-0"><Swords className="w-4 h-4" /></span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-ink">{t(ch.is_owner ? "vs_yours" : "vs_banner").replace("{name}", friendName(ch, t))}</span>
        <span className="block text-xs text-muted-foreground">{ch.score}/5 <span className="tracking-wider">{squares(ch.picks)}</span></span>
      </span>
    </div>
  );
}

const OUTCOMES = {
  win: { Icon: Trophy, tone: "border-oasis bg-oasis/10 text-oasis" },
  draw: { Icon: Handshake, tone: "border-stoneLine bg-sand/60 text-ink" },
  lose: { Icon: Swords, tone: "border-ochre/50 bg-ochre/10 text-ochre" },
};
const outcomeOf = (me, friend) => (me > friend ? "win" : me === friend ? "draw" : "lose");

/** Posts the visitor's result to the friend's challenge exactly once (never for owner / expired / already recorded). */
function useAcceptChallenge(id, ch, picks, day) {
  const qc = useQueryClient();
  const [posted, setPosted] = useState(false);
  useEffect(() => {
    if (!ch || ch.expired || ch.is_owner || ch.my_result || posted || ch.day !== day) return;
    setPosted(true);
    fetch(`${API}/api/daily/challenge/${id}/result`, { method: "POST", headers: headers(), body: JSON.stringify({ picks }) })
      .then((r) => r.ok && r.json())
      .then((d) => { if (d) qc.setQueryData(["challenge", id], d); trackEvent("vs_accepted", { beat: d && d.my_result?.score > d.score }); })
      .catch((err) => warn("challenge result failed:", err));
  }, [ch, id, picks, posted, qc, day]);
}

function ScoreColumn({ label, score, picks, highlight }) {
  return (
    <div className={`flex-1 rounded-xl p-3 text-center ${highlight ? "bg-card border border-stoneLine" : ""}`}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground truncate">{label}</div>
      <div className="stat-number text-3xl text-ink">{score}<span className="text-sm text-muted-foreground">/5</span></div>
      <div className="text-sm tracking-wider">{squares(picks)}</div>
    </div>
  );
}

/** Side-by-side comparison once the visitor has finished; posts the result the first time. */
export function Versus({ id, picks, day }) {
  const { t } = useLang();
  const { data: ch } = useChallenge(id);
  useAcceptChallenge(id, ch, picks, day);
  if (!ch || ch.expired || ch.day !== day) return null;
  if (ch.is_owner) {
    return (
      <div className="w-full max-w-sm rounded-2xl border border-stoneLine bg-sand/60 p-3 text-sm text-ink flex items-center gap-2" data-testid="vs-owner-stats">
        <Users className="w-4 h-4 text-ochre" /> {t("vs_stats").replace("{n}", ch.accepted).replace("{k}", ch.beaten)}
      </div>
    );
  }
  const me = picks.filter(Boolean).length;
  const outcome = outcomeOf(me, ch.score);
  const { Icon, tone } = OUTCOMES[outcome];
  const name = friendName(ch, t);
  return (
    <div className={`w-full max-w-md rounded-2xl border p-3 flex flex-col gap-3 ${tone}`} data-testid="vs-result" data-outcome={outcome}>
      <div className="flex items-center justify-center gap-2 font-semibold text-sm"><Icon className="w-4 h-4" /> {t(`vs_${outcome}`).replace("{name}", name)}</div>
      <div className="flex gap-2">
        <ScoreColumn label={t("vs_you")} score={me} picks={picks} highlight={outcome !== "lose"} />
        <ScoreColumn label={name} score={ch.score} picks={ch.picks} highlight={outcome === "lose"} />
      </div>
    </div>
  );
}

/** "Challenge a friend": creates (or reuses) today's share link and offers WhatsApp / copy / native share. */
export default function ChallengeFriend({ day, picks, rematch = false }) {
  const { t, lang } = useLang();
  const [ch, setCh] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/daily/challenge`, { method: "POST", headers: headers(), body: JSON.stringify({ day, picks, nickname: storage.get("atlas.nickname") || null }) });
      if (r.ok) { setCh(await r.json()); trackEvent("vs_created", { rematch }); }
    } catch (err) { warn("challenge create failed:", err); }
    setBusy(false);
  };
  if (!ch) {
    return (
      <button onClick={create} disabled={busy} data-testid="vs-create" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-ochre text-white text-sm font-medium hover:bg-ochre/90 disabled:opacity-60 transition-colors">
        <Swords className="w-4 h-4" /> {t(rematch ? "vs_rematch" : "vs_create")}
      </button>
    );
  }
  const url = `${window.location.origin}/quiz?daily=1&vs=${ch.id}&lang=${lang}`;
  const shareUrl = `${window.location.origin}/api/share/challenge/${ch.id}?lang=${lang}`;
  const text = t("vs_share_text").replace("{score}", `${ch.score}/5`).replace("{n}", ch.number);
  const links = socialLinks(shareUrl, text);
  const track = (m) => trackEvent("share", { method: m, content_type: "friend_challenge", lang });
  const copy = async () => { track("copy"); await copyText(`${text}\n${url}`); setCopied(true); setTimeout(() => setCopied(false), 1800); };
  return (
    <div className="w-full max-w-md rounded-2xl border border-ochre/40 bg-ochre/5 p-3 flex flex-col gap-2" data-testid="vs-share">
      <div className="text-xs font-semibold text-ink flex items-center gap-1.5"><Swords className="w-3.5 h-3.5 text-ochre" /> {t("vs_ready")}</div>
      <div className="flex items-center gap-2 rounded-xl border border-stoneLine bg-card px-3 py-2">
        <span className="flex-1 truncate text-xs text-muted-foreground" dir="ltr" data-testid="vs-url">{url}</span>
        <button onClick={copy} data-testid="vs-copy" className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-ochre">{copied ? <Check className="w-3.5 h-3.5 text-oasis" /> : <Link2 className="w-3.5 h-3.5" />} {copied ? t("copied") : ""}</button>
      </div>
      <div className="flex flex-wrap gap-2">
        <a href={links.whatsapp} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp")} data-testid="vs-whatsapp" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#25D366]/15 text-[#128C7E] text-xs font-medium"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</a>
        <a href={links.telegram} target="_blank" rel="noopener noreferrer" onClick={() => track("telegram")} data-testid="vs-telegram" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#229ED9]/15 text-[#1a7fb0] text-xs font-medium">Telegram</a>
        {canNativeShare() && <button onClick={() => { track("native"); nativeShare({ title: text, text, url: shareUrl }); }} data-testid="vs-native" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-ink text-sand text-xs font-medium"><Share2 className="w-3.5 h-3.5" /> {t("share")}</button>}
      </div>
    </div>
  );
}
